import type {useSortable} from '@github-ui/drag-and-drop'
import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {type ComponentProps, createContext, memo, useMemo} from 'react'
import {forwardRef} from 'react'

import {getVisibleCells} from '../../hooks/tanstack-table/helpers/row-api'
import type {Row} from '../../hooks/tanstack-table/helpers/types'
import {useTableSelectedRowIds} from '../../hooks/tanstack-table/hooks/use-table-row-selection'
import {useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import {getHovercardSubjectTag} from '../../models/memex-item-model'
import {useReorderableRow} from './row-reordering/reorderable-rows'
import {TableCell} from './table-cell'
import type {TableDataType} from './table-data-type'
import styles from './table-row.module.css'
import {rowTestId} from './test-identifiers'
import type {ItemDataType} from './use-item-data'
import {getSelectionInfo, isRowActionable} from './use-row-selection'

export type TableRowProps = {
  row: Row<TableDataType>
  index: number
  data: ItemDataType
}

export const StyledTableRow = forwardRef<HTMLDivElement, ComponentProps<'div'>>(({className, ...props}, ref) => {
  return <div ref={ref} className={clsx(className, styles.StyledTableRow)} {...props} />
})
StyledTableRow.displayName = 'StyledTableRow'

interface RowContext {
  rowNumber: number
  setSortActivatorNodeRef?: (node: HTMLElement | null) => void
  sortListeners?: ReturnType<typeof useSortable>['listeners']
}

export const RowContext = createContext<RowContext>({rowNumber: 0})

export const TableRow = memo((props: TableRowProps) => {
  const {flatRows, groupedRows} = useTableInstance()
  const selectedRowIds = useTableSelectedRowIds()

  const {setNodeRef, setActivatorNodeRef, listeners, isSorting, isDragging, isDropped} = useReorderableRow(props.row)

  // Because the row reference provided to TableRow does not change when a row is selected, bringing in the stateful
  // `useTableSelectedRowIds` from table state ensures that rows and their children render with the correct selection
  // state.
  const isSelected = Boolean(selectedRowIds?.[props.row.id]) && isRowActionable(props.row)
  const {beforeSelected, afterSelected, firstInGroup, lastInGroup} = getSelectionInfo(props.row, flatRows, groupedRows)
  const className = clsx({
    hoverable: !isSorting,
    'row-selected': isSelected,
    'row-highlight': isDragging || isDropped,
    'selection-top-edge': isSelected && (!beforeSelected || firstInGroup),
    'selection-bottom-edge': isSelected && (!afterSelected || lastInGroup),
  })

  const context = useMemo(
    () => ({
      rowNumber: props.index + 1,
      setSortActivatorNodeRef: setActivatorNodeRef,
      sortListeners: listeners,
    }),
    [props.index, setActivatorNodeRef, listeners],
  )
  return (
    <RowContext.Provider value={context}>
      <StyledTableRow
        role="row"
        className={className}
        {...testIdProps(rowTestId(props.row.index))}
        data-test-row-is-selected={isSelected}
        data-hovercard-subject-tag={getHovercardSubjectTag(props.row.original)}
        ref={setNodeRef}
      >
        {getVisibleCells(props.row).map(cell => {
          return (
            <TableCell
              row={cell.row}
              column={cell.column}
              key={cell.column.id}
              focusCellElement={props.data.focusCellElement}
              isDragging={isSorting}
            />
          )
        })}
      </StyledTableRow>
    </RowContext.Provider>
  )
})

TableRow.displayName = 'TableRow'

try{ RowContext.displayName ||= 'RowContext' } catch {}